"use client";

import { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { isOptedOutClient } from "@/lib/tracking/optOut";
import { initMetaPixel, loadFbeventsScript } from "@/lib/tracking/storefrontEvents";
import { isPixelExcludedPath } from "@/lib/marketing/pixelExclusions";

/**
 * Pixel de Meta y pixel de TikTok para la superficie de marketing. Sin ellos no
 * hay retargeting ni medición de los anuncios (estrategia de captación §5.1).
 *
 * Cada uno se carga SOLO si su ID está configurado; sin variables, el componente
 * no pinta nada. Los hosts están en el CSP de next.config.ts (script-src,
 * img-src, connect-src). En el App Router la navegación no recarga la página,
 * así que el PageView se dispara también al cambiar de ruta.
 *
 * Privacidad (auditoría legal 2026-10-14):
 * - Nada carga hasta comprobar en el navegador que el visitante NO se excluyó:
 *   ni Global Privacy Control ni la cookie `redy_optout` del enlace «Your
 *   Privacy Choices» del pie (lib/tracking/optOut.ts, el mismo de las tiendas).
 * - Meta se inicia con `autoConfig` apagado (initMetaPixel): sin eventos
 *   automáticos ni advanced matching que lean formularios como /diagnostico.
 * - Sin <noscript>: sin JavaScript no hay forma de leer la exclusión.
 * - Las rutas cuya URL lleva un secreto (lib/marketing/pixelExclusions.ts, p. ej.
 *   el link de agenda del socio) no cargan el píxel ni cuentan PageView.
 */
const META_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID?.trim() || null;
const TIKTOK_ID = process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID?.trim() || null;

type PixelWindow = Window & {
  fbq?: (...args: unknown[]) => void;
  ttq?: { page?: () => void };
};

export default function MarketingPixels() {
  const pathname = usePathname();
  // null = todavía no se sabe (primer render y SSR): no se carga nada.
  const [allowed, setAllowed] = useState<boolean | null>(null);
  // El primer PageView sale con la carga del pixel; el efecto de ruta solo
  // cubre las navegaciones client-side posteriores, para no contarla dos veces.
  const firstRun = useRef(true);
  // Entró por una URL con secreto: el píxel no carga en toda la visita.
  const landedOnSecret = useRef(isPixelExcludedPath(pathname));

  useEffect(() => {
    if (!META_ID && !TIKTOK_ID) return;
    if (landedOnSecret.current) return;
    let ok = false;
    try {
      ok = !isOptedOutClient(document, navigator as { globalPrivacyControl?: boolean });
    } catch {
      ok = false; // sin cookies legibles no hay cómo saberlo: no se carga
    }
    setAllowed(ok);
    if (ok && META_ID) {
      const w = window as unknown as Parameters<typeof initMetaPixel>[0];
      if (initMetaPixel(w, META_ID)) loadFbeventsScript(document);
    }
  }, []);

  useEffect(() => {
    if (allowed !== true) return;
    if (isPixelExcludedPath(pathname)) return;
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const w = window as PixelWindow;
    if (META_ID && typeof w.fbq === "function") w.fbq("track", "PageView");
    if (TIKTOK_ID && w.ttq?.page) w.ttq.page();
  }, [pathname, allowed]);

  if (allowed !== true || !TIKTOK_ID) return null;

  return (
    <Script id="tiktok-pixel" strategy="afterInteractive">
      {`!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};ttq.load('${TIKTOK_ID}');ttq.page();}(window,document,'ttq');`}
    </Script>
  );
}
